<template>
    <div id="canisters-split-widget-component">
        <WidgetWrapperComponent
            :class="is_widget_open ? 'widget-full-height' : ''"
            :title="$t('product_report.canisters_split')"
            id-widget="product-report-split"
            v-on:toggle="is_widget_open = $event"
        >
            <template v-slot:default>
                <highcharts v-if="totals.bidons_total" :options="donut_options" />
                <p v-else class="text-center font-color-medium py-6 mb-0">{{ $t('product_report.no_contract_information') }}</p>

                <div v-if="totals.bidons_total" class="d-flex flex-wrap justify-center gap-5 font-14">
                    <div class="d-flex align-center gap-1">
                        <span :style="{ backgroundColor: consumed_color }" class="legend-dot" />
                        {{ $t('product_report.consumed') }}
                        <b>{{ totals.bidons_consumed }}</b>
                        ({{ number(totals.consumed_percent || 0) }} %)
                    </div>
                    <div class="d-flex align-center gap-1">
                        <span :style="{ backgroundColor: remaining_color }" class="legend-dot" />
                        {{ $t('product_report.remaining') }}
                        <b>{{ totals.bidons_remaining }}</b>
                        ({{ number(totals.remaining_percent || 0) }} %)
                    </div>
                </div>

                <p v-if="pace" class="text-center font-14 font-color-medium mt-3 mb-0">
                    <span class="font-weight-bold text-uppercase">{{ $t('product_report.consumption_pace') }}</span>
                    —
                    {{
                        $t('product_report.pace_detail', {
                            consumed: number(pace.consumed_percent),
                            elapsed: number(pace.elapsed_percent),
                        })
                    }}
                </p>
            </template>
        </WidgetWrapperComponent>
    </div>
</template>

<script>
import moment from 'moment'
import WidgetWrapperComponent from '@/components/global/wrapper/WidgetWrapperComponent.vue'
import number from '@/mixin/number'

export default {
    name: 'CanistersSplitWidgetComponent',
    components: {
        WidgetWrapperComponent,
    },
    mixins: [number],
    props: {
        totals: {
            type: Object,
            required: true,
        },
        contract: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            is_widget_open: true,
            consumed_color: '#1B3A6B',
            remaining_color: '#7CB342',
        }
    },
    computed: {
        donut_options() {
            return {
                chart: { type: 'pie', height: 260 },
                exporting: { enabled: false },
                title: { text: '' },
                credits: { enabled: false },
                subtitle: {
                    useHTML: true,
                    text: `<div class="text-center"><span class="font-weight-bold" style="font-size: 45px">${
                        this.totals.bidons_total
                    }</span><br><span class="font-12 font-color-medium">${this.$tc('product_report.total_contract_canisters', [
                        this.totals.bidons_total,
                    ])}</span></div>`,
                    floating: true,
                    verticalAlign: 'middle',
                    y: 0,
                },
                tooltip: { pointFormat: '<b>{point.y}</b> ({point.percentage:.1f}%)' },
                plotOptions: {
                    pie: { innerSize: '70%', dataLabels: { enabled: false }, borderWidth: 0 },
                },
                series: [
                    {
                        name: this.$t('product_report.canisters'),
                        colorByPoint: true,
                        data: [
                            {
                                name: this.$t('product_report.consumed'),
                                y: this.totals.bidons_consumed,
                                color: this.consumed_color,
                            },
                            {
                                name: this.$t('product_report.remaining'),
                                y: this.totals.bidons_remaining || 0,
                                color: this.remaining_color,
                            },
                        ],
                    },
                ],
            }
        },

        // Consumption pace: share of the contract dotation consumed vs share of the contract duration elapsed,
        // so the client can tell whether consumption is on track.
        pace() {
            let pace = null

            if (this.contract.date && this.totals.consumed_percent !== null) {
                const begin = moment(this.contract.date)
                const end = this.contract.date_end
                    ? moment(this.contract.date_end)
                    : begin.clone().add(this.contract.duration_months || 0, 'month')
                const total = end.diff(begin)

                if (total > 0) {
                    pace = {
                        consumed_percent: this.totals.consumed_percent,
                        elapsed_percent: Math.min(Math.max(moment().diff(begin) / total, 0), 1) * 100,
                    }
                }
            }

            return pace
        },
    },
}
</script>
